import { ColumnConfiguration } from './types';

const EMPTY_COLUMN_VALUE = 'Not Available';

/**
 * Returns a string representation of a column value.
 *
 * @param columnValue - The value to extract and convert. Can be a primitive or an object with a `value` property.
 * @param returnRawValue - If true, returns the raw col value; otherwise returns a default empty column value for unsupported types.
 * @returns The string representation of the column value, a default empty value, or `undefined` depending on the input and options.
 */
export const getColumnValue = (columnValue: unknown, returnRawValue?: boolean): string => {
  if (typeof columnValue === 'object' && 'value' in columnValue) {
    const value = (columnValue as { value: unknown }).value;
    if (value == null) return returnRawValue ? undefined : EMPTY_COLUMN_VALUE;
    if (typeof value === 'string' || typeof value === 'number') {
      return String(value);
    }
    return returnRawValue ? (value as string) : EMPTY_COLUMN_VALUE;
  }
  if (typeof columnValue === 'string' || typeof columnValue === 'number') {
    // Supporting only string and number in primitive as of now.
    return String(columnValue);
  }
  return returnRawValue ? undefined : EMPTY_COLUMN_VALUE;
};

export const getSearchableColumns = <T extends object>(
  columnConfig: ColumnConfiguration[],
  excludeKey: keyof T
): string[] => {
  if (!Array.isArray(columnConfig)) {
    return [];
  }
  const hasSearchable = columnConfig.some(col => col.isSearchable);
  const columns = hasSearchable ? columnConfig.filter(col => col.isSearchable) : columnConfig;
  return columns.filter(col => col.columnKey !== excludeKey).map(col => col.columnKey);
};

export const getFilteredData = <T extends object>(
  data: T[],
  searchTerm: string,
  columnConfig: ColumnConfiguration[],
  excludeKey: keyof T
) => {
  const normalizedQuery = searchTerm.trim().toLowerCase();
  if (!normalizedQuery) return data;
  const searchableColumns = getSearchableColumns(columnConfig, excludeKey as string);
  return data.filter(row =>
    searchableColumns.some((colKey: string) => {
      const text = getColumnValue(row[colKey]);
      return text ? text.toLowerCase().includes(normalizedQuery) : false;
    })
  );
};
